<template>
  <base-form ref="form" v-model="isFormValid" :class="`tw-grid tw-grid-cols-${colSpan} tw-gap-x-4 tw-gap-y-1`">
    <base-text-field label="Name" placeholder="Enter name" v-model="name" v-if="!hideName" :class="`tw-col-span-${colSpan}`" required />
    <div :class="`tw-relative tw-col-span-${colSpan}`">
      <label
        :class="[
          'tw-absolute tw--top-1 tw-left-2 tw--mt-px tw-inline-block tw-px-1 tw-bg-white tw-text-xs tw-font-normal tw-z-20 tw-rounded',
          { 'tw-text-red-500': !!stripeElementsNumberError },
        ]"
        >Card Number</label
      >
      <div
        ref="stripeElementsNumber"
        class="tw-relative tw-border tw-rounded-md tw-ring-0 tw-transition-all tw-ease-in-out tw-duration-100 tw-bg-white tw-mt-1 tw-z-10 tw-cursor-text"
      />
      <div ref="stripeElementsNumberError" class="tw-text-red-500 tw-text-xs tw-h-5 tw-font-thin">
        <transition
          enter-class="tw--translate-y-full"
          enter-active-class="tw-ease-out tw-duration-300"
          enter-to-class="tw-translate-y-0"
          leave-class="tw-translate-y-0"
          leave-active-class="tw-ease-in tw-duration-100"
          leave-to-class="tw--translate-y-full"
        >
          <div class="tw-transition-transform tw-transform" v-show="stripeElementsNumberError">{{ stripeElementsNumberError }}</div>
        </transition>
      </div>
    </div>
    <div class="tw-relative">
      <label
        :class="[
          'tw-absolute tw--top-1 tw-left-2 tw--mt-px tw-inline-block tw-px-1 tw-bg-white tw-text-xs tw-font-normal tw-z-20 tw-rounded',
          { 'tw-text-red-500': !!stripeElementsExpirationError },
        ]"
        >Exp</label
      >
      <div
        ref="stripeElementsExpiration"
        class="tw-relative tw-border tw-rounded-md tw-ring-0 tw-transition-all tw-ease-in-out tw-duration-100 tw-bg-white tw-mt-1 tw-z-10 tw-cursor-text"
      />
      <div ref="stripeElementsExpirationError" class="tw-text-red-500 tw-text-xs tw-h-5 tw-font-thin">
        <transition
          enter-class="tw--translate-y-full"
          enter-active-class="tw-ease-out tw-duration-300"
          enter-to-class="tw-translate-y-0"
          leave-class="tw-translate-y-0"
          leave-active-class="tw-ease-in tw-duration-100"
          leave-to-class="tw--translate-y-full"
        >
          <div class="tw-transition-transform tw-transform" v-show="stripeElementsExpirationError">{{ stripeElementsExpirationError }}</div>
        </transition>
      </div>
    </div>
    <div class="tw-relative">
      <label
        :class="[
          'tw-absolute tw--top-1 tw-left-2 tw--mt-px tw-inline-block tw-px-1 tw-bg-white tw-text-xs tw-font-normal tw-z-20 tw-rounded',
          { 'tw-text-red-500': !!stripeElementsCvcError },
        ]"
        >CVV / CVC</label
      >
      <div
        ref="stripeElementsCvc"
        class="tw-relative tw-border tw-rounded-md tw-ring-0 tw-transition-all tw-ease-in-out tw-duration-100 tw-bg-white tw-mt-1 tw-z-10 tw-cursor-text"
      />
      <div ref="stripeElementsCvcError" class="tw-text-red-500 tw-text-xs tw-h-5 tw-font-thin">
        <transition
          enter-class="tw--translate-y-full"
          enter-active-class="tw-ease-out tw-duration-300"
          enter-to-class="tw-translate-y-0"
          leave-class="tw-translate-y-0"
          leave-active-class="tw-ease-in tw-duration-100"
          leave-to-class="tw--translate-y-full"
        >
          <div class="tw-transition-transform tw-transform" v-show="stripeElementsCvcError">{{ stripeElementsCvcError }}</div>
        </transition>
      </div>
    </div>
    <base-text-field label="Postal Code" placeholder="Enter postal code" v-model="postalCode" v-if="!hidePostalCode" required />
  </base-form>
</template>

<script>
import { loadStripe } from "@stripe/stripe-js/pure";

export default {
  props: {
    hideName: Boolean,
    hidePostalCode: Boolean,
  },
  data() {
    return {
      isFormValid: true,
      stripeToken: null,
      stripe: null,
      stripeElements: null,
      stripeElementsStyle: null,
      stripeElementsNumberError: null,
      stripeElementsExpirationError: null,
      stripeElementsCvcError: null,
      name: null,
      postalCode: null,
    };
  },
  computed: {
    colSpan() {
      return this.hidePostalCode ? 2 : 3;
    },
  },
  async mounted() {
    await this.loadStripeElements();

    this.$nextTick(() => {
      this.mountStripeElements();
    });
  },
  methods: {
    async loadStripeElements() {
      this.stripe = await loadStripe(this.$env.stripe_publishable_key);

      this.stripeElements = this.stripe.elements({
        fonts: [
          {
            cssSrc: "https://fonts.googleapis.com/css?family=Roboto:300,400,500,700",
          },
        ],
      });
      this.stripeElementsStyle = {
        base: {
          color: "rgb(17, 24, 39)",
          fontWeight: 300,
          fontFamily: 'Roboto, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
          fontSize: "14px",

          "::placeholder": {
            color: "rgb(107, 114, 128)",
          },
        },
        invalid: {
          color: "rgb(239, 68, 68)",
          "::placeholder": {
            color: "rgb(248, 113, 113)",
          },
        },
      };

      this.stripeElementsNumber = this.stripeElements.create("cardNumber", { style: this.stripeElementsStyle });
      this.stripeElementsNumber.addEventListener("change", (event) => (this.stripeElementsNumberError = event.error ? event.error.message : ""));

      this.stripeElementsExpiration = this.stripeElements.create("cardExpiry", { style: this.stripeElementsStyle });
      this.stripeElementsExpiration.addEventListener("change", (event) => (this.stripeElementsExpirationError = event.error ? event.error.message : ""));

      this.stripeElementsCvc = this.stripeElements.create("cardCvc", { style: this.stripeElementsStyle });
      this.stripeElementsCvc.addEventListener("change", (event) => (this.stripeElementsCvcError = event.error ? event.error.message : ""));
    },
    mountStripeElements() {
      this.stripeElementsNumber.mount(this.$refs.stripeElementsNumber);
      this.stripeElementsExpiration.mount(this.$refs.stripeElementsExpiration);
      this.stripeElementsCvc.mount(this.$refs.stripeElementsCvc);
    },
    async createToken() {
      const response = await this.stripe.createToken(this.stripeElementsNumber, {
        name: this.name || "",
        address_zip: this.postalCode || "",
        address_country: "US",
        currency: "usd",
      });
      this.$refs.form.validate();
      if (response.error) {
        this.stripeElementsNumberError = response.error.message;
        return null;
      }
      if (!this.isFormValid) {
        return null;
      }
      return response.token;
    },
    reset() {
      this.$refs.form.reset();
      this.stripeElementsNumber.clear();
      this.stripeElementsExpiration.clear();
      this.stripeElementsCvc.clear();
    },
  },
};
</script>

<style>
.StripeElement {
  @apply tw-py-2.5 tw-px-3;
}
.StripeElement--focus {
  @apply tw-ring-1 tw-ring-primary-500 tw-border-primary-500;
}

.StripeElement--invalid {
  @apply tw-border-red-500;
}

.StripeElement--invalid::placeholder {
  @apply tw-text-red-500;
}

.StripeElement--focus.StripeElement--invalid {
  @apply tw-ring-1 tw-ring-red-500;
}
</style>
